import { BaseCatalogItemTransformer } from './baseCatalogItem.transformer';
import type { Exhibition } from '@/types';
import type { CatalogType } from '../services/catalog/catalog-types';
import { getMediaData } from '@/lib/utils/media.utils';
import { processTagArray, getYearTag, getRelationTag, combineTags } from '@/lib/utils/tagGenerator';
import { hasRichTextContent } from '@/lib/utils/lexical-to-html';

/**
 * Transformer for converting Exhibition entities to CatalogItem
 */
export class ExhibitionTransformer extends BaseCatalogItemTransformer<Exhibition> {
  protected getId(exhibition: Exhibition): string | number {
    return exhibition.id;
  }

  protected getType(_exhibition: Exhibition): CatalogType {
    return 'exhibition';
  }

  protected getSlug(exhibition: Exhibition): string {
    return exhibition.slug;
  }

  protected getHref(exhibition: Exhibition): string | undefined {
    // içerik varsa link
    const hasContent =
      (exhibition.layout && exhibition.layout.length > 0) ||
      hasRichTextContent(exhibition.about) ||
      (exhibition.description && exhibition.description.trim().length > 0) ||
      (exhibition.photos && exhibition.photos.length > 0) ||
      (exhibition.videos && exhibition.videos.length > 0) ||
      (exhibition.highlights && exhibition.highlights.length > 0);

    if (hasContent) {
      return `/catalog/exhibitions/${exhibition.slug}`;
    }

    return undefined;
  }

  protected getTitle(exhibition: Exhibition): string {
    return exhibition.title;
  }

  protected getDisplayTitle(exhibition: Exhibition): string {
    return exhibition.title;
  }

  protected getFilename(exhibition: Exhibition): string {
    return exhibition.slug;
  }

  protected getThumbnail(exhibition: Exhibition): string {
    const cardImageData = getMediaData(exhibition.cardImage);
    return cardImageData.url || '';
  }

  protected getImageUrl(exhibition: Exhibition): string {
    const cardImageData = getMediaData(exhibition.cardImage);
    const coverImageData = exhibition.coverImage ? getMediaData(exhibition.coverImage) : null;
    const thumbnail = cardImageData.url || '';
    return coverImageData?.url || thumbnail;
  }

  protected getWidth(exhibition: Exhibition): number {
    const cardImageData = getMediaData(exhibition.cardImage);
    return cardImageData.width || 800;
  }

  protected getHeight(exhibition: Exhibition): number {
    const cardImageData = getMediaData(exhibition.cardImage);
    return cardImageData.height || 600;
  }

  protected getDate(exhibition: Exhibition): string {
    return this.getFormattedDate(exhibition) || exhibition.duration?.startDate || '';
  }

  protected getYear(exhibition: Exhibition): number {
    if (exhibition.duration && exhibition.duration.startDate) {
      const startDate = new Date(exhibition.duration.startDate);
      return startDate.getFullYear();
    }
    return new Date().getFullYear();
  }

  protected getDescription(exhibition: Exhibition): string {
    return exhibition.description;
  }

  protected getLongDescription(exhibition: Exhibition): string {
    return exhibition.description;
  }

  protected getLocation(exhibition: Exhibition): string | undefined {
    return exhibition.location || undefined;
  }

  protected getDuration(exhibition: Exhibition): string | undefined {
    return this.getFormattedDate(exhibition) || undefined;
  }

  protected getVideoUrl(exhibition: Exhibition): string | undefined {
    return exhibition.virtualTourUrl || undefined;
  }

  protected getTags(exhibition: Exhibition): string[] {
    return combineTags(
      processTagArray(exhibition.tags),
      getRelationTag(exhibition.type),
      getYearTag(exhibition.duration?.startDate)
    );
  }

  /**
   * Format date range for exhibitions
   */
  private getFormattedDate(exhibition: Exhibition): string {
    if (!exhibition.duration) return '';

    const startDate = new Date(exhibition.duration.startDate);
    const endDate = new Date(exhibition.duration.endDate);

    const startDay = startDate.getDate();
    const endDay = endDate.getDate();

    const startMonth = startDate.toLocaleString('en-US', { month: 'long' });
    const endMonth = endDate.toLocaleString('en-US', { month: 'long' });

    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();

    if (startYear === endYear) {
      if (startMonth === endMonth) {
        return `${startDay}-${endDay} ${startMonth} ${startYear}`;
      } else {
        return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${startYear}`;
      }
    } else {
      return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`;
    }
  }
}

// Export singleton instance
export const exhibitionTransformer = new ExhibitionTransformer();